import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import ContractDocumentsPreview from '../components/ContractDocumentsPreview';
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Phone,
  Mail,
  MapPin,
  Building,
  Zap,
  Flame,
  FileText,
  Plus,
  Clock,
  Send,
  Upload,
  Download,
  Trash2,
  File
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  
  const [client, setClient] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [newNote, setNewNote] = useState('');
  const [documents, setDocuments] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => {
    fetchClient();
    fetchContracts();
    fetchUsers();
    fetchNotes();
    fetchActivities();
    fetchDocuments();
  }, [id]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${id}`);
      setClient(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento del cliente');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/contracts?cliente_id=${id}`);
      setContracts(response.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data.filter(u => ['sub_agent', 'admin', 'super_admin'].includes(u.ruolo)));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notes/client/${id}`);
      setNotes(response.data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/activities/client/${id}`);
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${id}/documents`);
      setDocuments(response.data);
    } catch (error) {}
  };

  const handleUploadDocument = async (e, tipoDoc) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/api/clients/${id}/documents?tipo_documento=${tipoDoc}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento caricato');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleDownloadDoc = async (docId, filename) => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/${docId}/download`, {
        responseType: 'blob',
      });
      const contentType = res.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([res.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      // Se PDF/immagine apri inline, altrimenti scarica
      const inlineTypes = ['application/pdf', 'image/'];
      const isInline = inlineTypes.some((t) => contentType.startsWith(t));
      if (isInline) {
        window.open(blobUrl, '_blank');
      } else {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename || `documento-${docId}`;
        a.click();
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel download del documento');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`);
      toast.success('Documento eliminato');
      fetchDocuments();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API_URL}/api/clients/${id}`, editData);
      toast.success('Cliente aggiornato');
      setEditing(false);
      fetchClient();
      fetchActivities();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    try {
      await axios.post(`${API_URL}/api/notes`, {
        entita_tipo: 'client',
        entita_id: id,
        contenuto: newNote
      });
      setNewNote('');
      fetchNotes();
      fetchActivities();
      toast.success('Nota aggiunta');
    } catch (error) {
      toast.error('Errore nell\'aggiunta della nota');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatoColor = (stato) => {
    const colors = {
      bozza: 'bg-slate-100 text-slate-700',
      documenti_richiesti: 'bg-amber-100 text-amber-700',
      documenti_ricevuti: 'bg-sky-100 text-sky-700',
      in_lavorazione: 'bg-violet-100 text-violet-700',
      inviata: 'bg-indigo-100 text-indigo-700',
      accettata: 'bg-emerald-100 text-emerald-700',
      attiva: 'bg-green-100 text-green-700',
      scartata: 'bg-red-100 text-red-700',
      annullata: 'bg-red-100 text-red-700'
    };
    return colors[stato] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/clients')} className="shrink-0">
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Indietro</span>
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate">
              {client?.tipo_cliente === 'business' && client?.ragione_sociale 
                ? client.ragione_sociale 
                : `${client?.nome} ${client?.cognome}`}
            </h1>
            <p className="text-slate-500 text-xs sm:text-sm">Cliente #{client?.id?.slice(0, 8)}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {editing ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
                <X className="w-4 h-4 mr-2" />
                Annulla
              </Button>
              <Button size="sm" onClick={handleSave}>
                <Save className="w-4 h-4 mr-2" />
                Salva
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Modifica
              </Button>
              <Button size="sm" onClick={() => navigate(`/contracts?cliente_id=${id}`)}>
                <Plus className="w-4 h-4 mr-2" />
                Nuova Pratica
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5" />
                Anagrafica Cliente
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nome</Label>
                    <Input
                      value={editData.nome || ''}
                      onChange={(e) => setEditData({...editData, nome: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cognome</Label>
                    <Input
                      value={editData.cognome || ''}
                      onChange={(e) => setEditData({...editData, cognome: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ragione Sociale</Label>
                    <Input
                      value={editData.ragione_sociale || ''}
                      onChange={(e) => setEditData({...editData, ragione_sociale: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tipo Cliente</Label>
                    <Select 
                      value={editData.tipo_cliente} 
                      onValueChange={(v) => setEditData({...editData, tipo_cliente: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="privato">Privato</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Telefono</Label>
                    <Input
                      value={editData.telefono || ''}
                      onChange={(e) => setEditData({...editData, telefono: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      value={editData.email || ''}
                      onChange={(e) => setEditData({...editData, email: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Città</Label>
                    <Input
                      value={editData.citta || ''}
                      onChange={(e) => setEditData({...editData, citta: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provincia</Label>
                    <Input
                      value={editData.provincia || ''}
                      onChange={(e) => setEditData({...editData, provincia: e.target.value})}
                      maxLength={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Codice Fiscale</Label>
                    <Input
                      value={editData.codice_fiscale || ''}
                      onChange={(e) => setEditData({...editData, codice_fiscale: e.target.value.toUpperCase()})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fornitore Attuale</Label>
                    <Input
                      value={editData.fornitore_attuale || ''}
                      onChange={(e) => setEditData({...editData, fornitore_attuale: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Consumo Annuo</Label>
                    <Input
                      value={editData.consumo_annuo || ''}
                      onChange={(e) => setEditData({...editData, consumo_annuo: e.target.value})}
                      placeholder="es. 3500 kWh"
                    />
                  </div>
                  {hasRole(['super_admin', 'admin']) && (
                    <div className="space-y-2">
                      <Label>Agente Assegnato</Label>
                      <Select 
                        value={editData.agente_assegnato || "none"} 
                        onValueChange={(v) => setEditData({...editData, agente_assegnato: v === "none" ? "" : v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Non assegnato</SelectItem>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="col-span-2 space-y-2">
                    <Label>Note</Label>
                    <textarea
                      className="w-full h-24 px-3 py-2 text-sm border rounded-md"
                      value={editData.note || ''}
                      onChange={(e) => setEditData({...editData, note: e.target.value})}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Telefono</p>
                        <p className="font-medium">{client?.telefono}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Email</p>
                        <p className="font-medium">{client?.email || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Località</p>
                        <p className="font-medium">
                          {client?.citta ? `${client.citta} (${client.provincia})` : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Building className="w-4 h-4 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Fornitore Attuale</p>
                        <p className="font-medium">{client?.fornitore_attuale || '-'}</p>
                      </div>
                    </div>
                    {client?.codice_fiscale && (
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="text-xs text-slate-500">Codice Fiscale</p>
                          <p className="font-mono text-sm">{client.codice_fiscale}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {client?.note && (
                    <div className="col-span-2 pt-4 border-t">
                      <p className="text-xs text-slate-500 mb-1">Note</p>
                      <p className="text-sm text-slate-700">{client.note}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documenti */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <File className="w-5 h-5" />
                Documenti ({documents.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                {[
                  { tipo: 'carta_identita', label: 'Carta d\'Identita\'' },
                  { tipo: 'codice_fiscale', label: 'Codice Fiscale' },
                  { tipo: 'bolletta', label: 'Bolletta' },
                  { tipo: 'iban', label: 'IBAN / Estratto Conto' },
                  { tipo: 'altro', label: 'Altro Documento' }
                ].map(({ tipo, label }) => (
                  <label key={tipo} className="flex items-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg hover:border-sky-400 hover:bg-sky-50/50 cursor-pointer transition-colors">
                    <Upload className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="text-sm text-slate-600 truncate">{label}</span>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.odt,.ods,.txt,.rtf,.zip,.heic,.heif"
                      className="hidden"
                      onChange={(e) => handleUploadDocument(e, tipo)}
                      disabled={uploadingDoc}
                    />
                  </label>
                ))}
              </div>
              {documents.length === 0 ? (
                <p className="text-center text-slate-400 text-sm py-4">Nessun documento caricato</p>
              ) : (
                <div className="space-y-2">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`doc-${doc.id}`}>
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-red-500" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{doc.nome_file}</p>
                          <p className="text-xs text-slate-400">
                            {({
                              carta_identita: "Carta d'Identita'",
                              codice_fiscale: 'Codice Fiscale',
                              bolletta: 'Bolletta',
                              iban: 'IBAN / Estratto Conto',
                            }[doc.tipo_documento]) || 'Altro'}
                            {' - '}{(doc.size / 1024).toFixed(0)} KB
                            {' - '}{new Date(doc.created_at).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownloadDoc(doc.id, doc.nome_file)}>
                          <Download className="w-4 h-4" />
                        </Button>
                        {hasRole(['super_admin', 'admin']) && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => handleDeleteDoc(doc.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Anteprima inline documenti + OCR */}
          <ContractDocumentsPreview
            clientId={id}
            refreshKey={documents.length}
            onClientRefresh={fetchClient}
          />

          {/* Contracts */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Pratiche ({contracts.length})
              </CardTitle>
              <Button size="sm" onClick={() => navigate(`/contracts/new?cliente_id=${id}`)}>
                <Plus className="w-4 h-4 mr-1" />
                Nuova
              </Button>
            </CardHeader>
            <CardContent>
              {contracts.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Nessuna pratica</p>
              ) : (
                <div className="space-y-3">
                  {contracts.map((contract) => (
                    <div 
                      key={contract.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                      onClick={() => navigate(`/contracts/${contract.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        {contract.tipologia === 'luce' ? (
                          <Zap className="w-5 h-5 text-amber-500" />
                        ) : contract.tipologia === 'gas' ? (
                          <Flame className="w-5 h-5 text-sky-500" />
                        ) : (
                          <div className="flex">
                            <Zap className="w-4 h-4 text-amber-500" />
                            <Flame className="w-4 h-4 text-sky-500 -ml-1" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-sm">
                            {contract.fornitore || 'Fornitore N/D'} - {contract.offerta || 'Offerta N/D'}
                          </p>
                          <p className="text-xs text-slate-500 capitalize">{contract.tipologia}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatoColor(contract.stato)}`}>
                        {contract.stato.replace(/_/g, ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes and Activities */}
          <Tabs defaultValue="notes">
            <TabsList>
              <TabsTrigger value="notes">Note ({notes.length})</TabsTrigger>
              <TabsTrigger value="activities">Storico ({activities.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="notes" className="mt-4">
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Aggiungi una nota..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                    />
                    <Button onClick={handleAddNote}>
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    {notes.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nessuna nota</p>
                    ) : (
                      notes.map((note) => (
                        <div key={note.id} className="p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">{note.autore_nome}</span>
                            <span className="text-xs text-slate-500">{formatDate(note.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-700">{note.contenuto}</p>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="activities" className="mt-4">
              <Card>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {activities.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Nessuna attività</p>
                    ) : (
                      activities.map((activity) => (
                        <div key={activity.id} className="flex items-start gap-3 p-3 border-l-2 border-slate-200">
                          <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-slate-900">{activity.azione}</p>
                            {activity.dettagli && (
                              <p className="text-sm text-slate-500">{activity.dettagli}</p>
                            )}
                            <p className="text-xs text-slate-400 mt-1">
                              {activity.utente_nome} • {formatDate(activity.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informazioni</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Tipo Cliente</p>
                <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                  client?.tipo_cliente === 'business' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  {client?.tipo_cliente?.toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Agente Assegnato</p>
                <p className="font-medium">{client?.agente_nome || 'Non assegnato'}</p>
              </div>
              {client?.consumo_annuo && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Consumo Annuo</p>
                  <p className="font-medium">{client.consumo_annuo}</p>
                </div>
              )}
              {client?.scadenza_offerta && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Scadenza Offerta</p>
                  <p className="font-medium">{client.scadenza_offerta}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Date</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-slate-500">Creato il</p>
                <p className="text-sm font-medium">{formatDate(client?.created_at)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Ultimo aggiornamento</p>
                <p className="text-sm font-medium">{formatDate(client?.updated_at)}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;
